import { atom } from 'jotai'
import type { TCart } from '../types/TCart'

/**
 * Main cart state atom
 * Stores array of cart items with product and quantity
 */
export const cartState = atom<TCart[]>([])

/**
 * Adds a product to cart or increases quantity if already exists
 */
export const addToCart = atom(null, (get, set, product: TCart) => {
    const cart = get(cartState)
    const existingProduct = cart.find(p => p.product.item_code === product.product.item_code)

    if (existingProduct) {
        set(
            cartState,
            cart.map(p => (p.product.item_code === product.product.item_code ? { ...p, quantity: p.quantity + 1 } : p))
        )
    } else {
        set(cartState, [...cart, product])
    }
})

/**
 * Removes a product from cart completely
 */
export const removeFromCart = atom(null, (get, set, product: TCart) => {
    const cart = get(cartState)
    set(
        cartState,
        cart.filter(p => p.product.item_code !== product.product.item_code)
    )
})

/**
 * Clears all items from cart
 */
export const clearCart = atom(null, (_, set) => {
    set(cartState, [])
})

/**
 * Updates cart item quantity
 * Validates against min (0) and max (qty_sellable) limits
 */
export const updateCart = atom(null, (get, set, product: TCart) => {
    const cart = get(cartState)

    // Validate quantity limits
    if (product.quantity > Number(product.product.qty_sellable)) {
        return
    }
    if (product.quantity < 0) {
        return
    }

    if (cart.length === 0) {
        set(cartState, [{ ...product, quantity: 1 }])
        return
    }

    set(
        cartState,
        cart.map(p => (p.product.item_code === product.product.item_code ? { ...p, quantity: product.quantity } : p))
    )
})

/**
 * Increases quantity of a cart item by 1
 * Will not exceed qty_sellable limit
 */
export const increaseCartItem = atom(null, (get, set, product: TCart) => {
    const cart = get(cartState)

    // Prevent exceeding sellable quantity
    if (product.quantity > Number(product.product.qty_sellable)) {
        return
    }

    set(
        cartState,
        cart.map(p => {
            if (p.product.item_code === product.product.item_code) {
                return { ...p, quantity: p.quantity + 1 }
            }
            return p
        })
    )
})

/**
 * Decreases quantity of a cart item by 1
 * @param removeIfLastOne - If true, removes item when quantity reaches 0
 */
export const decreaseCartItem = atom(null, (get, set, product: TCart, removeIfLastOne: boolean = true) => {
    const cart = get(cartState)
    const existing = cart.find(p => p.product.item_code === product.product.item_code)
    const currentQty = existing ? existing.quantity : 0

    if (currentQty === 1 && removeIfLastOne) {
        set(
            cartState,
            cart.filter(p => p.product.item_code !== product.product.item_code)
        )
    } else {
        set(
            cartState,
            cart.map(p => (p.product.item_code === product.product.item_code ? { ...p, quantity: p.quantity - 1 } : p))
        )
    }
})

/**
 * Derived atom: calculates total price of all items in cart
 */
export const totalPriceInCart = atom(get => {
    const cart = get(cartState)
    return cart.reduce((acc, curr) => acc + curr.product.price * curr.quantity, 0)
})

/**
 * Derived atom: calculates total PV (Point Value) of all items in cart
 */
export const totalPVInCart = atom(get => {
    const cart = get(cartState)
    return cart.reduce((acc, curr) => acc + curr.product.pv * curr.quantity, 0)
})
